import { EventNames, IAnalyticsAdapter, ISetCustomerIdentity } from 'state/analytics/types';
import LocalStorage from 'utils/cognito/storage';
import { StorageKeys } from 'utils/local-storage';
import { toLoyaltyIdentityValue } from 'utils/loyalty';

import {
  IElementClickedEventPayload,
  IItemAddedEventPayload,
  IItemQuantityDecrementedEventPayload,
  IItemQuantityIncrementedEventPayload,
  IItemRemovedEventPayload,
  IItemViewedEventPayload,
  IPurchaseEventPayload,
  TEvents,
} from '../../analytics/events';

import {
  adobeElementClickedEvent,
  adobeItemAddedEvent,
  adobeItemRemovedEvent,
  adobeItemViewedEvent,
  adobePurchaseEvent,
} from './adobe-events';
import { IAdobeEvent, TGTMDataLayer } from './types';
import { transformPurchaseData } from './utils';

export class AdobeAnalyticsAdapter implements IAnalyticsAdapter {
  private dataLayer: Array<IAdobeEvent<Record<string, any>>>;
  private customerId: string | null = null;
  private shouldDuplicateEventsToGTMDataLayer: boolean = false;
  private GTMDataLayer: TGTMDataLayer;

  private get loyaltyId() {
    return this.customerId ?? this.getCustomerIdFromLocalStorage();
  }

  constructor(shouldDuplicateEventsToGTMDataLayer: boolean) {
    this.dataLayer = window.adobeDataLayer || [];
    this.shouldDuplicateEventsToGTMDataLayer = shouldDuplicateEventsToGTMDataLayer;
    this.GTMDataLayer = window.dataLayer || [];
  }

  public logEvent({ eventName, payload }: TEvents): void {
    switch (eventName) {
      //we need to upgrade Typescript version to work with union types properly
      //and remove this type casting
      case EventNames.PURCHASE: {
        const purchasePayload = payload as IPurchaseEventPayload;
        const purchaseEventPayload = transformPurchaseData(purchasePayload);
        this.sendEventToDataLayer(
          adobePurchaseEvent({
            payload: purchaseEventPayload,
            loyaltyId: this.loyaltyId,
          })
        );
        break;
      }
      case EventNames.ELEMENT_CLICKED: {
        const elementClickedPayload = payload as IElementClickedEventPayload;
        this.sendEventToDataLayer(
          adobeElementClickedEvent({ payload: elementClickedPayload, loyaltyId: this.loyaltyId })
        );
        break;
      }
      case EventNames.ITEM_VIEWED: {
        const itemViewedPayload = payload as IItemViewedEventPayload;
        this.sendEventToDataLayer(
          adobeItemViewedEvent({
            payload: { id: itemViewedPayload.menuData.id, name: itemViewedPayload.menuData.name },
            loyaltyId: this.loyaltyId,
          })
        );

        break;
      }

      case EventNames.ITEM_ADDED:
      case EventNames.ITEM_QUANTITY_INCREMENTED: {
        const itemAddedPayload = payload as
          | IItemAddedEventPayload
          | IItemQuantityIncrementedEventPayload;
        const item = itemAddedPayload.cartEntry;
        this.sendEventToDataLayer(
          adobeItemAddedEvent({
            payload: {
              id: item._id,
              cartId: item.cartId,
              quantity: item.quantity,
              name: item.name,
            },
            loyaltyId: this.loyaltyId,
          })
        );
        break;
      }

      case EventNames.ITEM_REMOVED:
      case EventNames.ITEM_QUANTITY_DECREMENTED: {
        const itemRemovedPayload = payload as
          | IItemRemovedEventPayload
          | IItemQuantityDecrementedEventPayload;
        const item = itemRemovedPayload.cartEntry;
        this.sendEventToDataLayer(
          adobeItemRemovedEvent({
            payload: {
              id: item._id,
              cartId: item.cartId,
              quantity: item.quantity,
              name: item.name,
            },
            loyaltyId: this.loyaltyId,
          })
        );
        break;
      }

      default:
        break;
    }
  }

  private sendEventToDataLayer = (event: IAdobeEvent<Record<string, any>>): void => {
    this.dataLayer.push(event);

    if (this.shouldDuplicateEventsToGTMDataLayer) {
      this.GTMDataLayer.push(event);
    }
  };

  private getCustomerIdFromLocalStorage(): string | null {
    const user = LocalStorage?.getItem(StorageKeys.USER);
    if (user) {
      const userId = user.thLegacyCognitoId
        ? toLoyaltyIdentityValue(user.thLegacyCognitoId)
        : user.cognitoId;
      this.customerId = userId;
      return userId;
    }
    return null;
  }

  public setCustomerIdentity(customerData: ISetCustomerIdentity): void {
    this.customerId = customerData.customerId;
  }

  public logOutCustomer(): void {
    this.customerId = null;
  }
}

export default AdobeAnalyticsAdapter;
